import React, { useEffect, useRef } from 'react';
import ChatInputTool from '../../../components/v2/chat-input-tool';
import SuccessBanner from '../../../components/v2/success-banner';
import BotMessage from '../../../components/v2/bot-message';
import {
  ConfirmationButtons,
  StreakChallengePopup,
  StudyModeSelector,
  StudyModeModal
} from '../../../components/v2/action-buttons';
import UserMessage from '../../../components/v2/user-message';
import { useLayoutContext } from '../../../layouts/general-layout';
import { useOnboarding } from './useOnboarding';
import './onboarding.scss';

import { BigBlueMoon, msgAvatar, flameIcon } from '../../../assets/images/index';



// Component to render bot messages with HTML support (for bold text)



export default function V2Onboarding() {
  const { openModesModal, closeModesModal } = useLayoutContext();
  const chatContainerRef = useRef<HTMLElement>(null);
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [isGreeting2Done, setIsGreeting2Done] = React.useState(false);
  const [isMicDone, setIsMicDone] = React.useState(false);
  const [isChatDone, setIsChatDone] = React.useState(false);
  const [isAttachDone, setIsAttachDone] = React.useState(false);
  const [isStudyModeDone, setIsStudyModeDone] = React.useState(false);

  const {
    t,
    currentStep,
    userInfo,
    labels,
    showConfirmMessage,
    showMicMessage,
    showChatMessage,
    showAttachMessage,
    showStudyModeMessage,
    streakSelection,
    gt,
    formatValue,
    stepAtOrAfter,
    handleConfirmGrade,
    handleUpdateGrade,
    handleSelectStreak,
    handleSelectMode,
    handleSendMessage,
    chatItems,
    userInitials,
    isCorrectClicked,
    isChatDisabled,
    isFromProfileUpdate,
    isProfileIncomplete
  } = useOnboarding(scrollBottomRef);

  // Centralized "Slick" Auto-Scroll: 
  // We use multiple observers to ensure we track both box-size changes (dots to text)
  // and DOM changes (new elements mounting).
  useEffect(() => {
    const content = scrollContentRef.current;
    const container = chatContainerRef.current;
    if (!content || !container) return;

    const scrollToBottom = () => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    };

    // 1. Observe height growth (Expansion)
    const resizeObserver = new ResizeObserver(scrollToBottom);

    // 2. Observe new elements (Mounting)
    const mutationObserver = new MutationObserver(scrollToBottom);

    resizeObserver.observe(content);
    mutationObserver.observe(content, { childList: true, subtree: true });

    // 3. Initial & State-change scroll
    scrollToBottom();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [currentStep, chatItems]); // Re-sync on step changes and new messages

  return (
    <div className="v2-onboarding-layout">
      {/* Global V2 Header handled by MainLayout */}

      {/* 2. Interactive Chat Area */}
      <main className="v2-onboarding-chat" ref={chatContainerRef}>
        <div ref={scrollContentRef} className="v2-onboarding-chat-scroll-wrapper">
          <div className="v2-onboarding-chat__banner-container">
            <img src={BigBlueMoon} alt="Shoonit Mascot" className="v2-onboarding-banner" />
          </div>

          <div className="v2-onboarding-chat__spacer" />

          {chatItems.map((item, idx) => {
            switch (item.type) {
              case 'bot':
                return (
                  <BotMessage
                    key={item.id || idx}
                    text={item.text}
                    onComplete={item.onComplete}
                    skipAnimation={item.skipAnimation}
                    id={item.id || String(idx)}
                  />
                );
              case 'typing':
                return <BotMessage key={item.id || idx} isLoading playAudio={false} />;
              case 'user':
                return <UserMessage key={idx} text={item.text} firstName={userInfo?.first_name} lastName={userInfo?.last_name} />;
              case 'confirmation_buttons':
                return (
                  <div key={idx} className="v2-onboarding-chat__actions">
                    <ConfirmationButtons 
                      onCorrect={handleConfirmGrade} 
                      onUpdate={handleUpdateGrade} 
                      onlyUpdate={isProfileIncomplete}
                      disabledCorrect={isCorrectClicked}
                    />
                  </div>
                );
              case 'success_banner':
                return <SuccessBanner key={idx} points={item.points} animate={true} />;
              case 'streak_popup':
                return (
                  <div key={idx} className="v2-onboarding-chat__actions">
                    <StreakChallengePopup
                      selectedOption={streakSelection}
                      onSelectOption={handleSelectStreak}
                      disabled={currentStep !== 'STREAK'}
                    />
                  </div>
                );
              case 'streak_result':
                return streakSelection != null ? (
                  <div key={idx} className="v2-onboarding-chat__streak-result-centered">
                    <div className="v2-onboarding-chat__streak-icon-wrapper">
                      <img src={flameIcon} alt="fire" className="v2-onboarding-chat__streak-fire-icon" />
                    </div>
                    <h2 className="v2-onboarding-chat__streak-days">
                      {streakSelection} {t('days')}
                    </h2>
                    <p className="v2-onboarding-chat__streak-points-label">
                      +20 {t('points')}
                    </p>
                  </div>
                ) : null;
              case 'study_selector':
                return (
                  <div key={idx} className="v2-onboarding-chat__actions">
                    <StudyModeSelector selectedMode={null} onSelectMode={handleSelectMode} />
                  </div>
                );
              default:
                return null;
            }
          })}

          <div ref={scrollBottomRef} className="v2-onboarding-chat__scroll-anchor" />
          {/* Physical spacer to ensure last message isn't "stuck" to the input tool */}
          <div className="v2-onboarding-chat__bottom-spacer" />
        </div>
      </main>

      {/* 3. V2 Chat Footer - Visible only after confirmation */}
      {stepAtOrAfter('POINTS') && (
        <ChatInputTool
          showAttach={true}
          onSendMessage={handleSendMessage}
          onMicClick={() => console.log('mic')}
          onAttachClick={() => console.log('attach')}
          onStudyModeClick={openModesModal}
          disabled={isChatDisabled}

          // Highlights trigger from the moment the message starts typing until the next message starts
          highlightMic={showMicMessage && !showChatMessage}
          highlightInput={showChatMessage && !showAttachMessage}
          highlightAttach={showAttachMessage && !showStudyModeMessage}
          highlightStudyMode={showStudyModeMessage && currentStep !== 'DONE'}
        />
      )}

    </div>
  );
}